
interface ExplodeConfig {
    cloneTarget?: boolean,
    scale?: number, 
    duration?: number, 
    onStart?: Function,
    onComplete?: Function,
    behind?: boolean,
    delay?: number,
    tint?: number,
    texture?: string
}

const explode = (target: Phaser.GameObjects.Sprite, config: ExplodeConfig = {}) => {
    config = { ...{ cloneTarget: true, scale: 3, duration: 150 }, ...config };

    let scene = target.scene;
    let sprite = config.cloneTarget ? scene.add.sprite(target.x, target.y, config.texture ?? target.texture) : target;
        sprite.setScale(0, 0);
        sprite.alpha = target.alpha;
        sprite.tint = config.tint ?? target.tint;
        sprite.setDisplaySize(target.displayWidth, target.displayHeight);
        sprite.setOrigin(target.originX, target.originY);
        sprite.depth = config.behind ? sprite.depth - 1 : sprite.depth + 1;
        
        return new Promise((res, rej) => {
            scene.tweens.add({
                targets: sprite,
                scaleX: config.scale,
                scaleY: config.scale,
                alpha: 0,
                duration: config.duration,
                delay: config.delay ?? 0,
                onStart: () => {
                    sprite.alpha = target.alpha;
                    config.onStart?.();
                },
                onComplete: () => {
                    sprite.alpha = 0;
                    config.onComplete?.();
                    res(null);
                    sprite.destroy();
                },
            });
        })
}


interface ShrinkConfig {
    duration?: number, 
    onStart?: Function,
    onComplete?: Function,
    delay?: number
}

const shrink = (targets: Phaser.GameObjects.Sprite[], config: ShrinkConfig = {}) => {
    let scene = targets[0].scene;
    // let sprite = scene.add.sprite(target.x, target.y, config.texture ?? target.texture);
    //     sprite.alpha = 0;
    //     sprite.tint = config.tint ?? target.tint;
    //     sprite.setDisplaySize(target.displayWidth, target.displayHeight)
    //     sprite.setOrigin(target.originX, target.originY);
    //     sprite.depth = config.behind ? sprite.depth - 1 : sprite.depth + 1;
        return new Promise((res, rej) => {
            scene.tweens.add({
                targets: targets,
                scaleX: 0,
                scaleY: 0,
                alpha: 0,
                duration: config.duration ?? 150,
                delay: config.delay ?? 0,
                onStart: () => config.onStart?.(),
                onComplete: () => {
                    config.onComplete?.();
                    res(null);
                }
            });
        })
}

type SlideFadeConfig = {
    type?: 'enter' | 'exit',
    duration?: number,
    onStart?: Function,
    onComplete?: Function,
    onUpdate?: Function,
    delay?: number,
    ease?: string | Function | undefined
} & ({ x: number, y: number } | { x: number, y?: number } | { x?: number, y: number });

type SlideFadeTarget = { scene: Phaser.Scene, x: number, y: number, alpha: number  };
const slideFade = (target: SlideFadeTarget, config: SlideFadeConfig = { x: 0, y: -25, type: 'exit' }) => {
    let scene = target.scene;
        target.alpha = 1; //TODO: Remove?

    let delay = (config.delay ?? 0);
    let duration = (config.duration ?? 200);
    let fadeDelay = delay + (duration * .4);
    let fadeDuration = duration * .6;

    let startAlpha:number, endAlpha:number, startX:number, endX:number, startY:number, endY:number;
    if (config.type === 'enter'){
        startAlpha = 0;
        endAlpha = target.alpha;
        endX = target.x;
        endY = target.y;
        startX = endX + (config.x ?? 0);
        startY = endY + (config.y ?? 0);
    } else {
        startAlpha = target.alpha;
        endAlpha = 0;
        startX = target.x;
        startY = target.y;
        endX = startX + (config.x ?? 0);
        endY = startY + (config.y ?? 0);
    }

    //Apply any necessary starting properties
    target.alpha = startAlpha;
    target.x = startX;
    target.y = startY;

    return Promise.all([
        new Promise((res, rej) => {
            scene.tweens.add({
                //ease: Phaser.Math.Easing.Quartic.Out,
                ease: config.ease ?? Phaser.Math.Easing.Quadratic.Out,
                targets: target,
                x: endX,
                y: endY,
                //alpha: 0,
                duration: duration,
                delay: delay,
                onUpdate: (...args) => config.onUpdate?.(...args),
                onStart: (...args) => config.onStart?.(...args),
                onComplete: (...args) => {
                    config.onComplete?.(...args);
                    res(null);
                }
            });
        }),
        new Promise((res, rej) => {
            scene.tweens.add({
                ease: Phaser.Math.Easing.Quadratic.In,
                targets: target,
                alpha: endAlpha,
                duration: fadeDuration,
                delay: fadeDelay,
                onComplete: () => {
                    res(null);
                }
            });
        })
    ])
}


interface TintShimmerTarget {
    tint: number,
    scene: Phaser.Scene
}

const shimmerTints : number[] = [0xffffff, 0xff8484, 0x84ffb5, 0xcd84ff, 0xcd84ff, 0x68ffdb, 0xff6868];

const shimmerTint = (target: TintShimmerTarget) => {
    let count : number = 0;
    let originalTint = target.tint;
    const shimmer = () => {
        if (count % 10 === 0){
            target.tint = Phaser.Utils.Array.GetRandom(shimmerTints);
            //target.tint = Phaser.Display.Color.RandomRGB(75).color;
        } 
        count = Phaser.Math.Wrap(count, 0, 100);
    }

    return {
        start: () => {
            target.scene.events.on('update', shimmer);
        },
        stop: () => {
            target.tint = originalTint;
            target.scene.events.removeListener('update', shimmer);
        }
    }
}

interface ScatterTarget {
    scene: Phaser.Scene,
    x: number,
    y: number,
    alpha: number
}

interface ScatterOptions {
    duration?: number, 
    onUpdate?: Function,
    onStart?: Function,
    onComplete?: Function
}

const scatter = (targets: ScatterTarget[], opts: ScatterOptions = {}) => {
    if (targets.length > 0){
        const config = { duration: 750, delay: 0, ...opts };
        const scene = targets[0].scene;
        return Promise.all(targets.map(target => {
            return new Promise((res, rej) => {
                let randX = Phaser.Math.Between(0, scene.cameras.main.width);
                let randY = Phaser.Math.Between(0, scene.cameras.main.height);
                scene.tweens.add({
                    ease: Phaser.Math.Easing.Quartic.Out,
                    targets: [target],
                    alpha: 0,
                    x: randX,
                    y: randY,
                    duration: config.duration,
                    onUpdate: (...args) => config.onUpdate?.(...args),
                    onStart: (...args) => config.onStart?.(...args),
                    onComplete: (...args) => {
                        config.onComplete?.(...args);
                        res(null);
                    }
                });
            })
        }))
    }
}

interface GrowTextOptions {
    /**
     * When a key is provided, the text sprite will be cached and reused. If "enter" or "exit" is 
     * called while the sprite is already in the process of entering or exiting, the previous tween
     * will be stopped.
     */
    key?: string,
    x?: number,
    y?: number,
    enterDuration?: number, 
    exitDuration?: number,
    delay?: number,
    shimmer?: boolean,
    style?: Phaser.Types.GameObjects.Text.TextStyle
}

interface GrowTextEnterOptions {
    text?: string,
    shimmer?: boolean,
    style?: Phaser.Types.GameObjects.Text.TextStyle,
    tween?: Phaser.Types.Tweens.TweenBuilderConfig
}

interface GrowTextExitOptions {
    shimmer?: boolean,
    style?: Phaser.Types.GameObjects.Text.TextStyle,
    tween?: Phaser.Types.Tweens.TweenBuilderConfig
}

const GrowTextCache = new Map<string, Phaser.GameObjects.Text>();
const growText = (scene: Phaser.Scene, text: string, config: GrowTextOptions = {}) => {
    let opts = { enterDuration: 500, exitDuration: 3000, delay: 0, x: 0, y: 0, ...config };
    let style = { ...(opts.style ?? {}) };
    let textSprite = scene.add.text(opts.x, opts.y, text, style);
        textSprite.setAlpha(0);
        textSprite.setScale(0);
        textSprite.setOrigin(.5);

    let activeTween : Phaser.Tweens.Tween | null = null;

    let { start, stop } = shimmerTint(textSprite);
    const enter = (enterOpts: GrowTextEnterOptions = {}) : Promise<void> => {
        if (activeTween) activeTween.complete();

        textSprite.setAlpha(0);
        textSprite.setScale(0);
        textSprite.setText(enterOpts.text ?? text);
        textSprite.setStyle({ ...style, ...enterOpts.style });

        if (opts.shimmer || enterOpts.shimmer) {
            start();
        } else {
            stop();
        }
        return new Promise((res, rej) => {
            activeTween = scene.tweens.add({
                ease: Phaser.Math.Easing.Quartic.Out,
                targets: [textSprite],
                alpha: 1,
                scaleX: 1,
                scaleY: 1,
                duration: opts.enterDuration,
                ...(enterOpts.tween ?? {}),
                onComplete: (...params) => {
                    activeTween = null;
                    enterOpts.tween?.onComplete?.(...params);
                    res();
                }
            });
        })
    }

    const exit = (exitOpts: GrowTextExitOptions = {}) : Promise<void> => {
        if (activeTween) activeTween.complete();

        textSprite.setStyle({ ...style, ...exitOpts.style });

        return new Promise((res, rej) => {
            activeTween = scene.tweens.add({
                ease: Phaser.Math.Easing.Quartic.In,
                targets: [textSprite],
                alpha: 0,
                scaleX: 1.5,
                scaleY: 1.5,
                duration: opts.exitDuration,
                ...(exitOpts.tween ?? {}),
                onComplete: (...params) => {
                    stop();
                    activeTween = null;
                    exitOpts.tween?.onComplete?.(...params);
                    //textSprite.destroy();
                    res();
                }
            });
        })
    }

    const destroy = () => {
        activeTween?.stop();
        textSprite.destroy();
    }

    return {
        enter,
        exit,
        destroy
    }
}

export {
    explode,
    shrink,
    slideFade,
    shimmerTint,
    scatter,
    growText
}